import {useIntl} from 'react-intl';
import {useSelector, useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import {AppButton} from '@components/Core/buttons/AppButton/AppButton';
import {AppOutlinedButton} from '@components/Core/buttons/AppOutlinedButton/AppOutlinedButton';
import {ACCOUNT_PATH, ACCOUNT_TWO_FACTOR_AUTH_PATH} from '@constants/index';
import {useGet2FAEnforcementStatus} from '@hooks/useGet2FAEnforcementStatus';
import {useCurrentRoute} from '@hooks/useCurrentRoute';
import {
    selectWasDismissedThisSession,
    selectIsDismissing2FA,
    dismissTwoFactorSetup
} from '@components/AccountSettings/TwoFactorAuth/twoFactorSlice';
import type {AppDispatch} from '@client/index';
import './twoFactorEnforcementModal.less';

const TWO_FACTOR_SETUP_PATH = `${ACCOUNT_PATH}/${ACCOUNT_TWO_FACTOR_AUTH_PATH}`;

export default function TwoFactorEnforcementModal() {
    const intl = useIntl();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const currentRoute = useCurrentRoute();
    const {isEnforced, isBlocking, isLoading, wasDismissedInCurrentSession} = useGet2FAEnforcementStatus();
    const wasDismissedThisSession = useSelector(selectWasDismissedThisSession);
    const isDismissing = useSelector(selectIsDismissing2FA);

    const isOnSetupPage = currentRoute?.childPath === ACCOUNT_TWO_FACTOR_AUTH_PATH;
    const isOpen = isEnforced && !isBlocking && !isLoading && !wasDismissedThisSession && !wasDismissedInCurrentSession && !isOnSetupPage;

    function handleSetup() {
        navigate(TWO_FACTOR_SETUP_PATH);
    }

    async function handlePostpone() {
        await dispatch(dismissTwoFactorSetup());
    }

    if (!isOpen) {
        return null;
    }

    return (
        <Dialog
            open
            maxWidth="sm"
            fullWidth
            disableBackdropClick={isBlocking}
            disableEscapeKeyDown={isBlocking}
            classes={{paper: 'two-factor-enforcement-modal'}}
        >
            <DialogTitle>
                {intl.formatMessage({
                    id: 'page.account.twoFactorAuth.enforcementWarningTitle',
                    defaultMessage: ''
                })}
            </DialogTitle>
            <DialogContent>
                <p className="enforcement-modal-description">
                    {intl.formatMessage({id: 'page.account.twoFactorAuth.enforcementWarning1', defaultMessage: ''})}
                </p>
                <p className="enforcement-modal-description">
                    {intl.formatMessage({id: 'page.account.twoFactorAuth.enforcementWarning2', defaultMessage: ''})}
                </p>
                {!isBlocking && (
                    <p className="enforcement-modal-description enforcement-modal-postpone-hint">
                        {intl.formatMessage({
                            id: 'page.account.twoFactorAuth.enforcementModalPostponeHint',
                            defaultMessage: 'You can postpone it once.'
                        })}
                    </p>
                )}
            </DialogContent>
            <DialogActions>
                {!isBlocking && (
                    <AppOutlinedButton
                        onClick={handlePostpone}
                        disabled={isDismissing}
                        data-wdio="test-2fa-enforcement-postpone"
                    >
                        {isDismissing
                            ? intl.formatMessage({id: 'esl.generic.loading', defaultMessage: 'Loading...'})
                            : intl.formatMessage({
                                id: 'page.account.twoFactorAuth.enforcementModalPostponeButton',
                                defaultMessage: 'Set up later'
                            })}
                    </AppOutlinedButton>
                )}
                <AppButton
                    onClick={handleSetup}
                    data-wdio="test-2fa-enforcement-setup"
                >
                    {intl.formatMessage({
                        id: 'page.account.twoFactorAuth.enforcementModalSetupButton',
                        defaultMessage: 'Set up 2FA'
                    })}
                </AppButton>
            </DialogActions>
        </Dialog>
    );
}
